/**
 * useKpiOverview · 投放概览数据 Hook
 *
 * 接口契约：specs/多游戏投放对比页/api/overview.md
 *
 * 关键设计：
 * - **服务端排序**：sort_item / sort_model 由表头点击触发；默认 `all_creative` desc
 * - **前端分页**：接口返全量（≤15 条），hook 内对 games 做切片得到 `pagedGames`；
 *   `total` 取**有效行数**（total 非 null 的游戏数）作为分页 recordCount
 * - **need_link=true**：默认拉取环比数据
 * - **filter 引用稳定**（来自 useFilterCommon）即可作为 useMemo 依赖
 * - 抽出 `request` 函数，params 变化时触发
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { getKpiOverview } from '../services';
import { IFilter } from '../types/filter';
import {
  IKpiMax,
  IKpiOverviewItem,
  IKpiOverviewParams,
  IKpiOverviewResponse,
  TKpiSortItem,
} from '../types/overview';
import { TSortOrder } from '../types/common';

export interface IUseKpiOverviewArgs {
  filter: IFilter;
}

/** 排序状态 */
export interface IKpiSortState {
  item: TKpiSortItem;
  model: TSortOrder;
}

/** 前端分页信息 */
export interface IKpiPageInfo {
  page: number;
  page_size: number;
}

/** 默认排序：Active Ads 降序 */
const DEFAULT_SORT: IKpiSortState = {
  item: 'all_creative',
  model: 'desc',
};

/** 默认分页 */
const DEFAULT_PAGE_INFO: IKpiPageInfo = {
  page: 1,
  page_size: 10,
};

const EMPTY_DATA: IKpiOverviewResponse = {
  has_data_datetime: '',
  games: [],
};

export function useKpiOverview({ filter }: IUseKpiOverviewArgs) {
  const [data, setData] = useState<IKpiOverviewResponse>(EMPTY_DATA);
  const [sort, setSort] = useState<IKpiSortState>(DEFAULT_SORT);
  const [pageInfo, setPageInfo] = useState<IKpiPageInfo>(DEFAULT_PAGE_INFO);

  const { wrapperCancelRequest, loading } = useCancelRequestHook();

  /** 请求参数（filter / 排序变化重算；分页变化不触发请求） */
  const params = useMemo<IKpiOverviewParams | null>(() => {
    if (!filter?.start_time || !filter?.end_time || !filter?.game_ids?.length)
      return null;
    return {
      ...filter,
      need_link: true,
      sort_item: sort.item,
      sort_model: sort.model,
    };
  }, [filter, sort]);

  /** 实际发起请求 */
  const request = useCallback(
    (nextParams: IKpiOverviewParams) => {
      wrapperCancelRequest(getKpiOverview, nextParams)
        .then((response: any) => {
          if (!response) return;
          setData({
            has_data_datetime: response.has_data_datetime || '',
            games: response.games || [],
            max: response.max,
          });
        })
        .catch(() => {
          // 请求被取消或失败时由 wrapperCancelRequest 内部处理
        });
    },
    [wrapperCancelRequest],
  );

  /** params 变化时自动发请求 */
  useEffect(() => {
    if (!params) {
      setData(EMPTY_DATA);
      return;
    }
    request(params);
  }, [params, request]);

  /** 排序 / filter 变化时重置分页到第 1 页 */
  useEffect(() => {
    setPageInfo(DEFAULT_PAGE_INFO);
  }, [sort, filter]);

  /** 前端切片：当前页的 games */
  const pagedGames = useMemo(() => {
    const start = (pageInfo.page - 1) * pageInfo.page_size;
    return data.games.slice(start, start + pageInfo.page_size);
  }, [data.games, pageInfo]);

  return {
    /** 全量 games（已服务端排序，含 total = null 行） */
    games: data.games as IKpiOverviewItem[],
    /** 当前页 games（前端切片） */
    pagedGames,
    /** 4 指标各自的最大值（{ game_id, value }） */
    max: data.max as IKpiMax | undefined,
    hasDataDatetime: data.has_data_datetime,
    /** 总条数（用作分页 recordCount） */
    total: data.games.length,
    /** 排序状态 */
    sort,
    setSort,
    /** 分页状态 */
    pageInfo,
    setPageInfo,
    loading,
  };
}
